import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  setDoc,
  deleteDoc,
  updateDoc,
  query,
  where,
  orderBy as orderByClause,
  getCountFromServer,
  type FirestoreDataConverter,
  type WhereFilterOp,
  type DocumentData,
  type WithFieldValue,
  type SetOptions,
  type QueryConstraint,
  Timestamp,
  GeoPoint,
  DocumentReference,
  limit as limitClause,
} from 'firebase/firestore'
import { db } from '@/firebase/config'
import * as Sentry from '@sentry/vue'
import { Logger } from './logger'
import { useDebugStore } from '@/stores/debugStore'
import { buildClientQuery } from '@/utils/queryBuilder'
import { type QuerySpec } from '@/types/FirestoreQuery.ts'
import { type Query } from 'firebase/firestore'

// Non-db-writing logger for firestore operations
const firestoreLogger = new Logger('FirestoreService', false)

// Debug logging utility
const isDebugMode = import.meta.env.DEV || import.meta.env.VITE_DEBUG === 'true'
const debugLog = (
  method: string,
  args: { collectionName: string; [key: string]: unknown },
): void => {
  if (isDebugMode) {
    firestoreLogger.debug(`${args.collectionName}:${method}`, args)
  }
}

// Debug tracking utility
const trackDebugOperation = (
  method: string,
  collectionName: string,
  documentId?: string,
  data?: unknown,
): void => {
  if (isDebugMode) {
    try {
      // Check if we're in a browser environment with Pinia available
      if (typeof window !== 'undefined' && window.document) {
        const debugStore = useDebugStore()
        debugStore.addOperation({
          operation: `${method} operation on ${collectionName}`,
          collection: collectionName,
          documentId,
          data,
          method: method as
            | 'get'
            | 'set'
            | 'update'
            | 'delete'
            | 'add'
            | 'list'
            | 'query'
            | 'count'
            | 'runClientQuerySpec'
            | 'countClientQuerySpec',
        })
      }
      // In Node.js/test environment, skip debug tracking
    } catch {
      // Silently fail if debug store is not available
      // This is expected in test environments
    }
  }
}

function reportFirestoreError(
  error: unknown,
  context: {
    operation: string
    collection: string
    docId?: string
    data?: unknown
  },
) {
  Sentry.captureException(error, {
    tags: {
      operation: context.operation,
      collection: context.collection,
    },
    extra: {
      ...(context.docId ? { docId: context.docId } : {}),
      ...(context.data ? { data: context.data } : {}),
    },
  })
}

// --- Type Conversion Helpers ---

function convertFromFirestore(value: unknown): unknown {
  if (value instanceof Timestamp) return value.toDate()
  if (value instanceof GeoPoint) return { lat: value.latitude, lng: value.longitude }
  if (value instanceof DocumentReference) return value
  if (Array.isArray(value)) return value.map(convertFromFirestore)
  if (value && typeof value === 'object') {
    const result: Record<string, unknown> = {}
    for (const [k, v] of Object.entries(value)) {
      result[k] = convertFromFirestore(v)
    }
    return result
  }
  return value
}

export function convertToFirestore(data: unknown): DocumentData | null {
  // Handle null or undefined
  if (data === null || data === undefined) {
    return null
  }

  // Handle primitive types
  if (typeof data !== 'object') {
    return data as DocumentData
  }

  // Handle Date objects
  if (data instanceof Date) {
    return Timestamp.fromDate(data)
  }

  // Handle DocumentReference
  if (data instanceof DocumentReference) {
    return data
  }

  // Handle arrays
  if (Array.isArray(data)) {
    return data.map((item) => convertToFirestore(item)) as DocumentData
  }

  // Handle objects
  const result: Record<string, DocumentData | null> = {}
  for (const [key, value] of Object.entries(data)) {
    // Skip Vue's internal properties
    if (key.startsWith('_') || key.startsWith('$')) {
      continue
    }

    // Skip functions
    if (typeof value === 'function') {
      continue
    }

    // Convert the value, including undefined values
    const convertedValue = convertToFirestore(value)
    if (convertedValue !== null) {
      result[key] = convertedValue
    }
  }
  return result
}

function converter<T>(): FirestoreDataConverter<T & { id?: string }, DocumentData> {
  return {
    toFirestore(model: WithFieldValue<T & { id?: string }>, _options?: SetOptions): DocumentData {
      const { id: _id, ...rest } = model
      return convertToFirestore(rest) as DocumentData
    },
    fromFirestore(snapshot, options): T & { id: string } {
      const data = snapshot.data(options)
      return {
        ...(convertFromFirestore(data) as T),
        id: snapshot.id,
      }
    },
  }
}

export async function getCollection<T>(collectionName: string, tenantId?: string) {
  const collectionPath = await buildCollectionPath(collectionName, tenantId)
  return collection(db, collectionPath).withConverter(converter<T>())
}

// Helper function to determine if a collection is tenant-scoped
const TENANT_SCOPED_COLLECTIONS = new Set([
  'people',
  'organizations',
  'transaction_records',
  'vehicles',
  'yards',
  'commodity_types',
  'price_history',
  'inventory',
  'scale_readings',
  'scale_desks',
  'attachments',
  'alerts',
  'parties',
  'call_logs',
  'email',
  'comments',
  'tasks',
  'timeline_activities',
  'email_subscriptions',
  'email_templates',
  'cash_sessions',
  'email_lists',
  'notifications',
  'id_documents',
  'settings',
])

function isTenantScopedCollection(collectionName: string): boolean {
  return TENANT_SCOPED_COLLECTIONS.has(collectionName)
}

// Helper function to get tenant ID from auth store session
async function getTenantId(): Promise<string | undefined> {
  try {
    // Only try to access stores in browser environment
    if (typeof window !== 'undefined' && window.document) {
      // Dynamic import to avoid circular dependencies
      const { useAuthStore } = await import('@/stores/authStore')
      const authStore = useAuthStore()

      // Debug logging
      firestoreLogger.debug('getTenantId: checking auth state', {
        isAuthenticated: authStore.isAuthenticated,
        authTenant: authStore.authTenant,
        sessionTenantId: authStore.session.tenantId,
        loading: authStore.loading,
      })

      // Wait for session to be ready with tenant ID
      let attempts = 0
      while (!authStore.session.tenantId && attempts < 50) {
        // Wait up to 5 seconds for session to be ready
        await new Promise((resolve) => setTimeout(resolve, 100))
        attempts++

        // Log progress every 10 attempts
        if (attempts % 10 === 0) {
          firestoreLogger.debug('getTenantId: waiting for tenant ID', {
            attempts,
            authTenant: authStore.authTenant,
            sessionTenantId: authStore.session.tenantId,
            loading: authStore.loading,
          })
        }
      }

      if (!authStore.session.tenantId) {
        firestoreLogger.warn('getTenantId: no tenant ID found after waiting', {
          attempts,
          authTenant: authStore.authTenant,
          isAuthenticated: authStore.isAuthenticated,
          loading: authStore.loading,
        })
      }

      return authStore.session.tenantId
    }
  } catch (error) {
    // Silently fail if auth store is not available (e.g., in test environments)
    firestoreLogger.debug('getTenantId: auth store not available', { error })
  }
  return undefined
}

// Helper function to build collection path with tenant awareness
export async function buildCollectionPath(
  collectionName: string,
  tenantId?: string,
): Promise<string> {
  const effectiveTenantId = tenantId || (await getTenantId())

  firestoreLogger.debug('buildCollectionPath', {
    collectionName,
    providedTenantId: tenantId,
    effectiveTenantId,
    isTenantScoped: isTenantScopedCollection(collectionName),
  })

  if (isTenantScopedCollection(collectionName) && effectiveTenantId) {
    const path = `tenants/${effectiveTenantId}/${collectionName}`
    firestoreLogger.debug('buildCollectionPath: using tenant path', { path })
    return path
  }

  firestoreLogger.debug('buildCollectionPath: using global path', { collectionName })
  return collectionName
}

function withErrorLogging<TArgs extends unknown[], TResult>(
  fn: (...args: TArgs) => Promise<TResult>,
  label?: string,
): (...args: TArgs) => Promise<TResult> {
  return async (...args: TArgs): Promise<TResult> => {
    try {
      return await fn(...args)
    } catch (error) {
      firestoreLogger.error(`${label ?? fn.name} error`, { error, context: { args } })

      // Extract collection name and docId from args if available
      const collectionName = args[0] as string
      const docId = args[1] as string | undefined
      const data = args[2] as unknown | undefined

      reportFirestoreError(error, {
        operation: label ?? fn.name,
        collection: collectionName,
        docId,
        data,
      })

      throw error
    }
  }
}

// --- Main Service ---

export const firestoreService = {
  get: withErrorLogging(
    async <T>(collectionName: string, id: string, tenantId?: string): Promise<T | null> => {
      debugLog('get', { collectionName, id, tenantId })
      const col = await getCollection<T>(collectionName, tenantId)
      const ref = doc(col, id)
      const snap = await getDoc(ref)
      const result = snap.exists() ? snap.data() : null
      debugLog('get:complete', { collectionName, id, exists: snap.exists(), tenantId })

      // Track debug operation for single document retrieval
      if (result) {
        trackDebugOperation('get', collectionName, id, result)
      }

      return result
    },
    'get',
  ),

  set: withErrorLogging(
    async <T extends { id?: string }>(
      collectionName: string,
      data: T,
      tenantId?: string,
    ): Promise<void> => {
      debugLog('set', { collectionName, data, tenantId })
      if (!data.id) throw new Error('Document ID is required for set')
      const col = await getCollection<T>(collectionName, tenantId)
      const ref = doc(col, data.id)
      await setDoc(ref, data)
      debugLog('set:complete', { collectionName, id: data.id, tenantId })

      // Track debug operation for document creation/update
      trackDebugOperation('set', collectionName, data.id, data)
    },
    'set',
  ),

  update: withErrorLogging(
    async <T>(
      collectionName: string,
      id: string,
      data: Partial<T>,
      tenantId?: string,
    ): Promise<void> => {
      debugLog('update', { collectionName, id, data, tenantId })
      const col = await getCollection<T>(collectionName, tenantId)
      const ref = doc(col, id)

      // updateDoc is the only firestore function that doesn't use the converter automatically based on ref
      const convertedData = convertToFirestore(data) as DocumentData

      await updateDoc(ref, convertedData)
      debugLog('update:complete', { collectionName, id, tenantId })

      // Track debug operation for document update
      trackDebugOperation('update', collectionName, id, data)
    },
    'update',
  ),

  delete: withErrorLogging(
    async (collectionName: string, id: string, tenantId?: string): Promise<void> => {
      debugLog('delete', { collectionName, id, tenantId })
      const col = await getCollection(collectionName, tenantId)
      const ref = doc(col, id)
      await deleteDoc(ref)
      debugLog('delete:complete', { collectionName, id, tenantId })

      // Track debug operation for document deletion
      trackDebugOperation('delete', collectionName, id)
    },
    'delete',
  ),

  add: withErrorLogging(
    async <T>(collectionName: string, data: T, tenantId?: string): Promise<string> => {
      debugLog('add', { collectionName, data, tenantId })
      const col = await getCollection<T>(collectionName, tenantId)
      const ref = await addDoc(col, data)
      debugLog('add:complete', { collectionName, id: ref.id, tenantId })

      // Track debug operation for document creation
      trackDebugOperation('add', collectionName, ref.id, data)

      return ref.id
    },
    'add',
  ),

  list: withErrorLogging(async <T>(collectionName: string, tenantId?: string): Promise<T[]> => {
    debugLog('list', { collectionName, tenantId })
    const col = await getCollection<T>(collectionName, tenantId)
    const snap = await getDocs(col)
    const result = snap.docs.map((d) => d.data())
    debugLog('list:complete', { collectionName, count: result.length, tenantId })
    return result
  }, 'list'),

  query: withErrorLogging(
    async <T>(
      collectionName: string,
      conditions: Array<{ field: string; op: WhereFilterOp; value: unknown }> = [],
      orderBy?: [string, 'asc' | 'desc'],
      limit?: number,
      tenantId?: string,
    ): Promise<T[]> => {
      debugLog('query', { collectionName, conditions, orderBy, limit, tenantId })
      const col = await getCollection<T>(collectionName, tenantId)
      const constraints: QueryConstraint[] = conditions.map(({ field, op, value }) =>
        where(field, op, value),
      )
      if (orderBy) constraints.push(orderByClause(orderBy[0], orderBy[1]))
      if (limit) constraints.push(limitClause(limit))
      const q = query(col, ...constraints)
      const snap = await getDocs(q)
      const result = snap.docs.map((d) => d.data())
      debugLog('query:complete', { collectionName, count: result.length, tenantId })
      return result
    },
    'query',
  ),

  count: withErrorLogging(
    async (
      collectionName: string,
      conditions: Array<{ field: string; op: WhereFilterOp; value: unknown }> = [],
      tenantId?: string,
    ): Promise<number> => {
      debugLog('count', { collectionName, conditions, tenantId })
      const col = await getCollection(collectionName, tenantId)
      const constraints: QueryConstraint[] = conditions.map(({ field, op, value }) =>
        where(field, op, value),
      )
      const q = query(col, ...constraints)
      const snap = await getCountFromServer(q)
      const result = snap.data().count
      debugLog('count:complete', { collectionName, count: result, tenantId })
      return result
    },
    'count',
  ),

  docRef: async (
    collectionName: string,
    id: string,
    tenantId?: string,
  ): Promise<DocumentReference> => {
    debugLog('docRef', { collectionName, id, tenantId })
    const collectionPath = await buildCollectionPath(collectionName, tenantId)
    return doc(db, collectionPath, id)
  },

  runClientQuerySpec: withErrorLogging(
    async <T>(spec: QuerySpec<T>, tenantId?: string): Promise<T[]> => {
      debugLog('runClientQuerySpec', { collectionName: spec.collection, spec, tenantId })
      const t0 = performance.now()

      // Update the spec to use tenant-aware collection path
      const tenantAwareSpec = {
        ...spec,
        collection: await buildCollectionPath(spec.collection, tenantId),
      }

      const q = buildClientQuery<T>(db, tenantAwareSpec) as Query<T>
      const snap = await getDocs(q)
      const documents = snap.docs.map((d) => ({ id: d.id, ...d.data() }) as T)

      const duration = performance.now() - t0
      debugLog('runClientQuerySpec:complete', {
        collectionName: spec.collection,
        count: documents.length,
        duration: `${duration.toFixed(2)}ms`,
        queryName: spec.meta?.name,
        tenantId,
      })

      // Track debug operation for query execution
      trackDebugOperation('runClientQuerySpec', spec.collection, undefined, {
        queryName: spec.meta?.name,
        duration,
        count: documents.length,
      })

      return documents
    },
    'runClientQuerySpec',
  ),

  countClientQuerySpec: withErrorLogging(
    async <T>(spec: QuerySpec<T>, tenantId?: string): Promise<number> => {
      debugLog('countClientQuerySpec', { collectionName: spec.collection, spec, tenantId })
      const t0 = performance.now()

      // Update the spec to use tenant-aware collection path
      const tenantAwareSpec = {
        ...spec,
        collection: await buildCollectionPath(spec.collection, tenantId),
      }

      const q = buildClientQuery<T>(db, tenantAwareSpec) as Query<T>
      const snap = await getCountFromServer(q)
      const count = snap.data().count

      const duration = performance.now() - t0
      debugLog('countClientQuerySpec:complete', {
        collectionName: spec.collection,
        count,
        duration: `${duration.toFixed(2)}ms`,
        queryName: spec.meta?.name,
        tenantId,
      })

      // Track debug operation for count execution
      trackDebugOperation('countClientQuerySpec', spec.collection, undefined, {
        queryName: spec.meta?.name,
        duration,
        count,
      })

      return count
    },
    'countClientQuerySpec',
  ),

  // IFirestoreService interface compatibility method
  runQuery: withErrorLogging(async <T>(spec: QuerySpec<T>): Promise<Array<T & { id: string }>> => {
    debugLog('runQuery', { collectionName: spec.collection, spec })
    const t0 = performance.now()

    // Update the spec to use tenant-aware collection path
    const tenantAwareSpec = {
      ...spec,
      collection: await buildCollectionPath(spec.collection),
    }

    // Build query and apply converter for proper Timestamp/Date conversion
    const q = buildClientQuery<T>(db, tenantAwareSpec).withConverter(converter<T>())
    const snap = await getDocs(q)
    // Converter's fromFirestore already includes id and converts Timestamps to Dates
    const documents = snap.docs.map((d) => d.data() as T & { id: string })

    const duration = performance.now() - t0
    debugLog('runQuery:complete', {
      collectionName: spec.collection,
      count: documents.length,
      duration: `${duration.toFixed(2)}ms`,
      queryName: spec.meta?.name,
    })

    // Track debug operation for query execution
    trackDebugOperation('runQuery', spec.collection, undefined, {
      queryName: spec.meta?.name,
      duration,
      count: documents.length,
    })

    return documents
  }, 'runQuery'),
}
